import axios from "axios";
import * as R from "ramda";

import { Clearances, RadiusRules, ZoningClearances } from "types/entities";
import { X_TENANT_SLUG, X_TENANT_VERSION } from "utils/httpHeaderNames";

const createAxios = axios.create;

const responseToClearances = (r: any): Clearances =>
  R.reduceBy(
    (acc, c) => {
      const permission = relationshipForType(c, "permission", r);
      const rule = relationshipForType(c, "rule", r);
      return acc.concat({ permission, rule });
    },
    [],
    (c: any) => c.relationships.zone.data.id,
    r.data,
  );

const responseToRadiusRules = (r: any): RadiusRules => {
  if (!r.included) return {};

  const permissionForRuleID = (id) => {
    const clearance = R.find(R.pathEq(String(id), ["relationships", "rule", "data", "id"]), r.data);
    const permissionID = R.path(["relationships", "permission", "data", "id"], clearance) as number;

    const permission = R.find<any>(
      R.and(R.propEq("permissions", "type"), R.propEq(permissionID, "id")),
      r.included,
    );
    const category = R.path(["attributes", "category"], permission) as string;
    if (category) return category.toLowerCase().replace("permission::", "");
    return null;
  };

  const isRadiusRule = (r: any): boolean =>
    R.and(R.propEq("rules", "type", r), R.equals(R.props(["rule_type"], r.attributes), ["Radius"]));

  return R.pipe(
    R.filter(isRadiusRule),
    R.map((id) => Number(R.prop("id", id))),
    R.groupBy(permissionForRuleID),
  )(r.included);
};

const relationshipForType = (obj: any, type: string, response: any): any => {
  const id = R.path(["relationships", type, "data", "id"], obj);
  if (!id) return null;

  const relationship = R.find<any>(
    R.and(R.propEq(`${type}s`, "type"), R.propEq(id, "id")),
    response.included,
  )?.attributes;

  return R.mergeRight(relationship || {}, { id: Number(id) });
};

type ConfigureOptions = {
  locale: string;
  version: string;
  tenantSlug?: string | null;
  baseURL?: string | null;
};

class ZoningApi {
  public client!: ReturnType<typeof createAxios>;

  private locale: string;

  configure({ locale, version, tenantSlug = null, baseURL = null }: ConfigureOptions): void {
    this.locale = locale;
    this.client = createAxios({
      baseURL,
      headers: {
        Accept: "application/vnd.api+json",
        [X_TENANT_VERSION]: version,
        [X_TENANT_SLUG]: tenantSlug,
      },
    });
  }

  async getClearances(useCodeSlug: string): Promise<ZoningClearances> {
    const { data } = await this.client.get("/api/zoning/clearances", {
      params: this.withDefaultParams({
        "filter[use.slug]": useCodeSlug,
        include: "permission,rule,zone",
      }),
    });
    const clearances = responseToClearances(data) as Clearances;
    const radiusRules = responseToRadiusRules(data) as RadiusRules;
    return { clearances, radiusRules };
  }

  withDefaultParams(params) {
    params.locale ||= this.locale;
    return params;
  }
}
export type ZoningApiType = InstanceType<typeof ZoningApi>;

export default new ZoningApi();
